import {useMemo} from 'react';
import * as CGReact from './cg/cgreact';
import * as Op from './cg/ops';
import * as CG from './cg/graph';

type assetType =
  | 'image-file'
  | 'video-file'
  | 'audio-file'
  | 'html-file'
  | 'bokeh-file'
  | 'object3D-file';

export const useAssetURLFromArtifact = <
  InputNodeInternalType extends assetType,
  InputNodeType extends CG.Node<InputNodeInternalType>
>(
  inputNode: InputNodeType,
  opMediaArtifact: (
    inputs: CG.OpInputTypes<{asset: InputNodeInternalType}>
  ) => CG.OutputNode<'artifact'>
) => {
  const nodeValueQuery = CGReact.useNodeValue(inputNode);
  const artifactNode = useMemo(() => opMediaArtifact({asset: inputNode}), [
    inputNode,
    opMediaArtifact,
  ]);

  const directUrlNode = useMemo(() => {
    if (!nodeValueQuery.loading) {
      return Op.opFileDirectUrl({
        file: Op.opArtifactFile({
          artifact: artifactNode,
          path: Op.constString(nodeValueQuery.result.path),
        }),
      });
    } else {
      return CG.voidNode();
    }
  }, [artifactNode, nodeValueQuery]);
  const directUrlQuery = CGReact.useNodeValue(directUrlNode);

  return {
    loading: nodeValueQuery.loading || directUrlQuery.loading,
    asset: nodeValueQuery.result,
    directUrl: directUrlQuery.result,
  };
};

export const useAssetContentFromArtifact = <
  InputNodeInternalType extends assetType,
  InputNodeType extends CG.Node<InputNodeInternalType>
>(
  inputNode: InputNodeType,
  opMediaArtifact: (
    inputs: CG.OpInputTypes<{asset: InputNodeInternalType}>
  ) => CG.OutputNode<'artifact'>
) => {
  const nodeValueQuery = CGReact.useNodeValue(inputNode);
  const artifactNode = useMemo(() => opMediaArtifact({asset: inputNode}), [
    inputNode,
    opMediaArtifact,
  ]);
  const contentNode = useMemo(() => {
    if (!nodeValueQuery.loading) {
      return Op.opFileContents({
        file: Op.opArtifactFile({
          artifact: artifactNode,
          path: Op.constString(nodeValueQuery.result.path),
        }),
      });
    } else {
      return CG.voidNode();
    }
  }, [artifactNode, nodeValueQuery]);
  const contentQuery = CGReact.useNodeValue(contentNode);

  return {
    loading: nodeValueQuery.loading || contentQuery.loading,
    asset: nodeValueQuery.result,
    contents: contentQuery.result,
  };
};
